/**
 * Utility functions for debugging and diagnostics
 */

// Debug mode flag from environment
export const DEBUG_MODE = import.meta.env.VITE_DEBUG_MODE === 'true';

// Mock data flag from environment
export const USE_MOCK_DATA = import.meta.env.VITE_ENABLE_MOCK_DATA === 'true';

/**
 * Debug logger - only logs when debug mode is enabled
 */
export const debugLog = (context: string, message: string, data?: any) => {
  if (!DEBUG_MODE) return;
  
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const prefix = `[${timestamp}] [${context}]`;
  
  if (data !== undefined) {
    console.log(`${prefix} ${message}`, data);
  } else {
    console.log(`${prefix} ${message}`);
  }
};

/**
 * Network request logger
 */
export const logRequest = (method: string, url: string, data?: any) => {
  debugLog('API Request', `${method.toUpperCase()} ${url}`, data);
};

/**
 * Network response logger
 */
export const logResponse = (status: number, url: string, data?: any) => {
  debugLog('API Response', `${status} ${url}`, data);
};

/**
 * Network error logger
 */
export const logError = (context: string, error: any) => {
  if (!DEBUG_MODE) return;
  
  const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
  const prefix = `[${timestamp}] [${context}] ERROR:`;
  
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    console.error(`${prefix} Server responded with error ${error.response.status}`, {
      data: error.response.data,
      headers: error.response.headers
    });
  } else if (error.request) {
    // The request was made but no response was received
    console.error(`${prefix} No response received`, {
      request: error.request
    });
  } else {
    // Something happened in setting up the request
    console.error(`${prefix} Request setup error`, error.message);
  }
  
  console.error(`${prefix} Full error:`, error);
};

/**
 * Get a user-friendly error message from an API error
 */
export const getErrorMessage = (error: any): string => {
  if (error.response) {
    // Server responded with an error
    const { status, data } = error.response;
    
    // If the response has a detail message, use it
    if (data && data.detail) {
      return typeof data.detail === 'string' 
        ? data.detail 
        : JSON.stringify(data.detail);
    }
    
    // Generic messages based on status code
    switch (status) {
      case 400: return 'Bad Request: The server could not understand the request';
      case 401: return 'Unauthorized: Authentication is required';
      case 403: return 'Forbidden: You don\'t have permission to access this resource';
      case 404: return 'Not Found: The requested resource does not exist';
      case 405: return 'Method Not Allowed: This operation is not supported';
      case 408: return 'Request Timeout: The server timed out waiting for the request';
      case 429: return 'Too Many Requests: Rate limit exceeded';
      case 500: return 'Internal Server Error: The server encountered an unexpected condition';
      case 502: return 'Bad Gateway: The server received an invalid response from the upstream server';
      case 503: return 'Service Unavailable: The server is temporarily unavailable';
      case 504: return 'Gateway Timeout: The server timed out waiting for the upstream server';
      default: return `Server Error (${status}): An unexpected error occurred`;
    }
  }
  
  if (error.request) {
    // The request was made but no response was received
    return 'Network Error: Could not connect to the server. Please check your internet connection or the server may be down.';
  }
  
  // Something happened in setting up the request
  return error.message || 'Unknown Error: An unexpected error occurred';
};

/**
 * Mock data indicator component props
 */
export interface MockDataIndicatorProps {
  show: boolean;
  label?: string;
}

export default {
  DEBUG_MODE,
  USE_MOCK_DATA,
  debugLog,
  logRequest,
  logResponse,
  logError,
  getErrorMessage
};